import { supabase } from '../lib/supabase'
import { QualityCategory, QualityImprocedentReason, QualityParameter, QualityTicket, QualityTicketAttachment, QualityTicketComment, QualityTicketHistory, QualityTicketOrigin, QualityTicketStage, QualityTicketType, QualityTicketTypeOriginLink, Severity, TicketBroker, TicketKind } from '../types/quality'
import { logCreate, logDelete, logUpdate } from './userLogs'
import { dateOnlyFilterGte, dateOnlyFilterLte, dateOnlyToTimestamptz, parseDateOnlyYmd } from '../utils/dateOnly'
import { TICKET_ATTACHMENT_MAX_BYTES, TICKET_ATTACHMENT_MAX_MB } from '../pages/Qualidade/qualidadeAttachments'

/** Ordem da etapa atribuída automaticamente ao criar um novo ticket. */
export const INITIAL_TICKET_STAGE_ORDER_INDEX = 1

export function findTicketStageIdByOrder(
  stages: QualityTicketStage[],
  orderIndex: number = INITIAL_TICKET_STAGE_ORDER_INDEX,
): string | null {
  return stages.find(s => s.order_index === orderIndex)?.id ?? null
}

export interface TicketFilters {
  stageId?: string
  severity?: Severity
  kind?: TicketKind
  categoryId?: string
  departmentId?: string
  requesterUserId?: string
  ticketTypeId?: string
  originId?: string
  brokerId?: string
  policyNumber?: string
  convenioCode?: string
  dateFrom?: string
  dateTo?: string
  /**
   * Tickets excluídos logicamente (`deleted_at` preenchido).
   * Omitido ou `active`: apenas não excluídos (comportamento padrão da listagem).
   */
  deletedFilter?: 'active' | 'deleted' | 'all'
  /** Seleção múltipla (dashboard e filtros avançados). */
  stageIds?: string[]
  severities?: Severity[]
  kinds?: TicketKind[]
  categoryIds?: string[]
  departmentIds?: string[]
  requesterUserIds?: string[]
  ticketTypeIds?: string[]
  originIds?: string[]
  brokerIds?: string[]
  /** `active` + `deleted` juntos equivalem a `all`. */
  deletedFilters?: Array<'active' | 'deleted'>
}

function resolveDeletedFilterMode(f: TicketFilters): 'active' | 'deleted' | 'all' {
  if (f.deletedFilter) return f.deletedFilter
  const multi = f.deletedFilters
  if (!multi?.length) return 'active'
  const hasActive = multi.includes('active')
  const hasDeleted = multi.includes('deleted')
  if (hasActive && hasDeleted) return 'all'
  if (hasDeleted) return 'deleted'
  return 'active'
}

/** @deprecated Use TicketFilters */
export type IncidentFilters = TicketFilters

function snapshotCategoryFields(c: Partial<QualityCategory> | null | undefined): Record<string, unknown> {
  if (!c) return {}
  return {
    name: c.name ?? null,
    display_name: c.display_name ?? null,
    is_active: c.is_active ?? null,
    description: c.description ?? null,
  }
}

function snapshotStageFields(s: Partial<QualityTicketStage> | null | undefined): Record<string, unknown> {
  if (!s) return {}
  return {
    key: s.key ?? null,
    name: s.name ?? null,
    order_index: s.order_index ?? null,
    is_closed: s.is_closed ?? null,
    color: s.color ?? null,
  }
}

function normalizePolicyNumber(value: unknown): string | null {
  if (value == null || value === '') return null
  return String(value)
}

function normalizeTicketRow(ticket: QualityTicket): QualityTicket {
  return {
    ...ticket,
    policy_number: normalizePolicyNumber(ticket.policy_number),
  }
}

function snapshotTicketFields(i: Partial<QualityTicket> | null | undefined): Record<string, unknown> {
  if (!i) return {}
  return {
    ticket_number: i.ticket_number ?? null,
    title: i.title ?? null,
    stage_id: i.stage_id ?? null,
    category_id: i.category_id ?? null,
    department_id: i.department_id ?? null,
    severity: i.severity ?? null,
    occurred_at: i.occurred_at ?? null,
    description: i.description ?? null,
    justification: i.justification ?? null,
    resolution: i.resolution ?? null,
    owner_user_id: i.owner_user_id ?? null,
    registered_by_user_id: i.registered_by_user_id ?? null,
    kind: i.kind ?? 'incident',
    requester_user_id: i.requester_user_id ?? null,
    ticket_type_id: i.ticket_type_id ?? null,
    origin_id: i.origin_id ?? null,
    improcedent_reason_id: i.improcedent_reason_id ?? null,
    external_link: i.external_link ?? null,
    broker_id: i.broker_id ?? null,
    policy_number: i.policy_number ?? null,
    convenio_code: i.convenio_code ?? null,
    max_deadline_unit: i.max_deadline_unit ?? null,
    max_deadline_value: i.max_deadline_value ?? null,
    max_deadline_notes: i.max_deadline_notes ?? null,
  }
}

/** Ordenação da listagem de tickets (tela de tickets e dashboard): nome do corretor A→Z; sem corretor no fim; empate por data de criação (mais recente primeiro). */
function sortTicketsByBrokerName(tickets: QualityTicket[]): QualityTicket[] {
  return [...tickets].sort((a, b) => {
    const nameA = a.broker?.name?.trim() ?? ''
    const nameB = b.broker?.name?.trim() ?? ''
    const hasA = nameA.length > 0
    const hasB = nameB.length > 0
    if (!hasA && !hasB) {
      const da = a.created_at ?? ''
      const db = b.created_at ?? ''
      return db.localeCompare(da)
    }
    if (!hasA) return 1
    if (!hasB) return -1
    const cmp = nameA.localeCompare(nameB, 'pt-BR', { sensitivity: 'base' })
    if (cmp !== 0) return cmp
    const da = a.created_at ?? ''
    const db = b.created_at ?? ''
    return db.localeCompare(da)
  })
}

const TICKET_SELECT =
  '*, stage:stage_id(*), category:category_id(id,name,display_name), department:department_id(id,name,display_name), requester:requester_user_id(id,name), registered_by:registered_by_user_id(id,name), ticket_type:ticket_type_id(id,name,display_name), origin:origin_id(id,name,display_name), improcedent_reason:improcedent_reason_id(id,name,display_name), broker:broker_id(id,name,id_scgp)'

/** Mesmo formato de join usado em Configurações → Usuários (`department:departments`). */
export type ProfileWithDepartment = {
  id: string
  name: string | null
  department_id?: string | null
  department?: { id: string; name: string; display_name: string } | null
}

const PROFILE_WITH_DEPARTMENT_SELECT =
  'id, name, department_id, department:departments(id, name, display_name)'

function normalizeProfileRow(row: Record<string, unknown>): ProfileWithDepartment {
  let department = row.department as ProfileWithDepartment['department'] | ProfileWithDepartment['department'][] | null | undefined
  if (Array.isArray(department)) department = department[0] ?? null
  return {
    id: row.id as string,
    name: (row.name as string | null) ?? null,
    department_id: (row.department_id as string | null) ?? null,
    department: department ?? null,
  }
}

async function fetchAllDepartments(): Promise<{ id: string; name: string; display_name: string }[]> {
  const { data } = await supabase.from('departments').select('id, name, display_name').order('display_name')
  return (data || []) as { id: string; name: string; display_name: string }[]
}

function attachDepartmentsToProfiles(
  profiles: ProfileWithDepartment[],
  departments: { id: string; name: string; display_name: string }[],
): ProfileWithDepartment[] {
  const deptById = new Map(departments.map(d => [d.id, d]))
  return profiles.map(p => ({
    ...p,
    department: p.department ?? (p.department_id ? deptById.get(p.department_id) ?? null : null),
  }))
}

function enrichTicketsWithRequesterProfiles(
  tickets: QualityTicket[],
  profiles: ProfileWithDepartment[],
): QualityTicket[] {
  if (!tickets.length || !profiles.length) return tickets
  const byId = new Map(profiles.map(p => [p.id, p]))
  return tickets.map(t => {
    const uid = t.requester_user_id
    if (!uid) return t
    const profile = byId.get(uid)
    if (!profile) return t
    return {
      ...t,
      requester: {
        id: profile.id,
        name: t.requester?.name ?? profile.name,
        department: profile.department ?? null,
      },
    }
  })
}

async function loadProfilesWithDepartment(opts?: {
  ids?: string[]
  activeOnly?: boolean
}): Promise<ProfileWithDepartment[]> {
  const departments = await fetchAllDepartments()
  let query = supabase.from('profiles').select(PROFILE_WITH_DEPARTMENT_SELECT).order('name')
  if (opts?.ids?.length) query = query.in('id', opts.ids)
  if (opts?.activeOnly) query = query.eq('is_active', true)

  const { data, error } = await query
  if (error) {
    let fallbackQuery = supabase.from('profiles').select('id, name, department_id').order('name')
    if (opts?.ids?.length) fallbackQuery = fallbackQuery.in('id', opts.ids)
    if (opts?.activeOnly) fallbackQuery = fallbackQuery.eq('is_active', true)
    const { data: fallback } = await fallbackQuery
    return attachDepartmentsToProfiles(
      (fallback || []).map(r => normalizeProfileRow(r as Record<string, unknown>)),
      departments,
    )
  }
  return attachDepartmentsToProfiles(
    (data || []).map(r => normalizeProfileRow(r as Record<string, unknown>)),
    departments,
  )
}

export async function getQualityParameterValueByKey(key: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('quality_parameters')
    .select('value')
    .eq('key', key)
    .maybeSingle()
  if (error) {
    console.warn('[qualityService] getQualityParameterValueByKey error:', error)
    return null
  }
  return (data?.value as string | undefined) ?? null
}

export const qualityService = {
  // ---- Categories ----
  async listCategories(): Promise<QualityCategory[]> {
    const { data } = await supabase
      .from('quality_categories')
      .select('*')
      .is('deleted_at', null)
      .order('display_name', { ascending: true })
    return (data || []) as QualityCategory[]
  },

  async listCategoriesActive(): Promise<QualityCategory[]> {
    const { data } = await supabase
      .from('quality_categories')
      .select('*')
      .is('deleted_at', null)
      .eq('is_active', true)
      .order('display_name', { ascending: true })
    return (data || []) as QualityCategory[]
  },

  async listCategoriesDeleted(): Promise<QualityCategory[]> {
    const { data } = await supabase
      .from('quality_categories')
      .select('*, deleter:deleted_by(id, name)')
      .not('deleted_at', 'is', null)
      .order('deleted_at', { ascending: false })
    return (data || []) as any
  },

  async upsertCategory(category: Partial<QualityCategory>): Promise<QualityCategory | null> {
    const payload = { ...category }
    delete (payload as any).deleter

    let previous: QualityCategory | null = null
    if (payload.id) {
      const { data: existing } = await supabase
        .from('quality_categories')
        .select('*')
        .eq('id', payload.id)
        .is('deleted_at', null)
        .maybeSingle()
      previous = existing as QualityCategory | null
    }

    const { data, error } = await supabase
      .from('quality_categories')
      .upsert(payload, { onConflict: 'id' })
      .select('*')
      .single()
    if (error) throw error
    const row = data as QualityCategory

    if (previous) {
      void logUpdate('quality_category', row.id, `Qualidade: categoria "${row.display_name}" atualizada`, snapshotCategoryFields(previous) as Record<string, any>, snapshotCategoryFields(row) as Record<string, any>)
    } else {
      void logCreate('quality_category', row.id, `Qualidade: categoria "${row.display_name}" criada`, snapshotCategoryFields(row) as Record<string, any>)
    }
    return row
  },

  async deleteCategory(id: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    const { data: before } = await supabase
      .from('quality_categories')
      .select('id, name, display_name')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    const { error } = await supabase
      .from('quality_categories')
      .update({ deleted_at: new Date().toISOString(), deleted_by: user.id, is_active: false })
      .eq('id', id)
      .is('deleted_at', null)
    if (error) throw error

    const label = (before as QualityCategory | null)?.display_name || (before as QualityCategory | null)?.name || id
    void logDelete('quality_category', id, `Qualidade: categoria "${label}" excluída (exclusão lógica)`, snapshotCategoryFields(before as QualityCategory | null) as Record<string, any>)
  },

  // ---- Stages ----
  async listStages(): Promise<QualityTicketStage[]> {
    const { data } = await supabase
      .from('quality_ticket_stages')
      .select('*')
      .is('deleted_at', null)
      .order('order_index', { ascending: true })
    return (data || []) as QualityTicketStage[]
  },

  async getInitialTicketStageId(): Promise<string | null> {
    const { data } = await supabase
      .from('quality_ticket_stages')
      .select('id')
      .eq('order_index', INITIAL_TICKET_STAGE_ORDER_INDEX)
      .is('deleted_at', null)
      .maybeSingle()
    return data?.id ?? null
  },

  async listStagesDeleted(): Promise<QualityTicketStage[]> {
    const { data } = await supabase
      .from('quality_ticket_stages')
      .select('*, deleter:deleted_by(id, name)')
      .not('deleted_at', 'is', null)
      .order('deleted_at', { ascending: false })
    return (data || []) as any
  },

  async upsertStage(stage: Partial<QualityTicketStage>): Promise<QualityTicketStage | null> {
    const payload = { ...stage }
    delete (payload as any).deleter

    let previous: QualityTicketStage | null = null
    if (payload.id) {
      const { data: existing } = await supabase
        .from('quality_ticket_stages')
        .select('*')
        .eq('id', payload.id)
        .is('deleted_at', null)
        .maybeSingle()
      previous = existing as QualityTicketStage | null
    }

    const { data, error } = await supabase
      .from('quality_ticket_stages')
      .upsert(payload, { onConflict: 'id' })
      .select('*')
      .single()
    if (error) throw error
    const row = data as QualityTicketStage

    if (previous) {
      void logUpdate('quality_incident_stage', row.id, `Qualidade: etapa "${row.name}" atualizada`, snapshotStageFields(previous) as Record<string, any>, snapshotStageFields(row) as Record<string, any>)
    } else {
      void logCreate('quality_incident_stage', row.id, `Qualidade: etapa "${row.name}" criada`, snapshotStageFields(row) as Record<string, any>)
    }
    return row
  },

  async deleteStage(id: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    const { data: before } = await supabase
      .from('quality_ticket_stages')
      .select('id, key, name')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    const { error } = await supabase
      .from('quality_ticket_stages')
      .update({ deleted_at: new Date().toISOString(), deleted_by: user.id })
      .eq('id', id)
      .is('deleted_at', null)
    if (error) throw error

    const label = (before as QualityTicketStage | null)?.name || (before as QualityTicketStage | null)?.key || id
    void logDelete('quality_incident_stage', id, `Qualidade: etapa "${label}" excluída (exclusão lógica)`, snapshotStageFields(before as QualityTicketStage | null) as Record<string, any>)
  },

  // ---- Tickets ----
  async listTickets(filters?: TicketFilters): Promise<QualityTicket[]> {
    const f = filters || {}
    const deletedFilter = resolveDeletedFilterMode(f)
    const select =
      deletedFilter === 'active' ? TICKET_SELECT : `${TICKET_SELECT}, deleter:deleted_by(id, name)`

    let query = supabase.from('quality_tickets').select(select).order('created_at', { ascending: false })

    if (deletedFilter === 'active') query = query.is('deleted_at', null)
    else if (deletedFilter === 'deleted') query = query.not('deleted_at', 'is', null)

    if (f.stageIds?.length) query = query.in('stage_id', f.stageIds)
    else if (f.stageId) query = query.eq('stage_id', f.stageId)
    if (f.severities?.length) query = query.in('severity', f.severities)
    else if (f.severity) query = query.eq('severity', f.severity)
    if (f.kinds?.length) query = query.in('kind', f.kinds)
    else if (f.kind) query = query.eq('kind', f.kind)
    if (f.categoryIds?.length) query = query.in('category_id', f.categoryIds)
    else if (f.categoryId) query = query.eq('category_id', f.categoryId)
    if (f.departmentIds?.length) query = query.in('department_id', f.departmentIds)
    else if (f.departmentId) query = query.eq('department_id', f.departmentId)
    if (f.requesterUserIds?.length) query = query.in('requester_user_id', f.requesterUserIds)
    else if (f.requesterUserId) query = query.eq('requester_user_id', f.requesterUserId)
    if (f.ticketTypeIds?.length) query = query.in('ticket_type_id', f.ticketTypeIds)
    else if (f.ticketTypeId) query = query.eq('ticket_type_id', f.ticketTypeId)
    if (f.originIds?.length) query = query.in('origin_id', f.originIds)
    else if (f.originId) query = query.eq('origin_id', f.originId)
    if (f.brokerIds?.length) query = query.in('broker_id', f.brokerIds)
    else if (f.brokerId) query = query.eq('broker_id', f.brokerId)
    if (f.policyNumber) query = query.eq('policy_number', f.policyNumber)
    if (f.convenioCode) query = query.eq('convenio_code', Number(f.convenioCode))
    if (f.dateFrom) query = query.gte('occurred_at', dateOnlyFilterGte(f.dateFrom))
    if (f.dateTo) query = query.lte('occurred_at', dateOnlyFilterLte(f.dateTo))

    const { data, error } = await query
    if (error) throw error
    const tickets = ((data || []) as unknown as QualityTicket[]).map(normalizeTicketRow)
    const requesterIds = tickets.map(t => t.requester_user_id).filter((id): id is string => !!id)
    const profiles = await loadProfilesWithDepartment({ ids: requesterIds })
    return sortTicketsByBrokerName(enrichTicketsWithRequesterProfiles(tickets, profiles))
  },

  /** @deprecated Use listTickets */
  async listIncidents(filters?: TicketFilters): Promise<QualityTicket[]> {
    return this.listTickets(filters)
  },

  async getTicketById(id: string): Promise<QualityTicket | null> {
    const { data, error } = await supabase
      .from('quality_tickets')
      .select(TICKET_SELECT)
      .eq('id', id)
      .maybeSingle()
    if (error) throw error
    if (!data) return null
    const ticket = normalizeTicketRow(data as unknown as QualityTicket)
    const requesterIds = ticket.requester_user_id ? [ticket.requester_user_id] : []
    if (!requesterIds.length) return ticket
    const profiles = await loadProfilesWithDepartment({ ids: requesterIds })
    return enrichTicketsWithRequesterProfiles([ticket], profiles)[0]
  },

  async listTicketsByStage(stageId: string): Promise<QualityTicket[]> {
    const { data, error } = await supabase
      .from('quality_tickets')
      .select(TICKET_SELECT)
      .is('deleted_at', null)
      .eq('stage_id', stageId)
      .order('created_at', { ascending: false })
    if (error) throw error
    const tickets = ((data || []) as QualityTicket[]).map(normalizeTicketRow)
    const requesterIds = tickets.map(t => t.requester_user_id).filter((id): id is string => !!id)
    const profiles = await loadProfilesWithDepartment({ ids: requesterIds })
    return enrichTicketsWithRequesterProfiles(tickets, profiles)
  },

  /** @deprecated Use listTicketsByStage */
  async listIncidentsByStage(stageId: string): Promise<QualityTicket[]> {
    return this.listTicketsByStage(stageId)
  },

  async listTicketsDeleted(): Promise<QualityTicket[]> {
    const { data, error } = await supabase
      .from('quality_tickets')
      .select(TICKET_SELECT + ', deleter:deleted_by(id, name)')
      .not('deleted_at', 'is', null)
      .order('deleted_at', { ascending: false })
    if (error) throw error
    const tickets = ((data || []) as unknown as QualityTicket[]).map(normalizeTicketRow)
    const requesterIds = tickets.map(t => t.requester_user_id).filter((id): id is string => !!id)
    const profiles = await loadProfilesWithDepartment({ ids: requesterIds })
    return enrichTicketsWithRequesterProfiles(tickets, profiles)
  },

  /** @deprecated Use listTicketsDeleted */
  async listIncidentsDeleted(): Promise<QualityTicket[]> {
    return this.listTicketsDeleted()
  },

  async listActiveProfilesForPicker(): Promise<ProfileWithDepartment[]> {
    return loadProfilesWithDepartment({ activeOnly: true })
  },

  /**
   * Busca corretores por nome ou código SCGP.
   * Padrão: mín. 4 chars. Com `allowEmpty`, lista até 50 ao abrir sem digitar.
   */
  async searchBrokers(
    query: string,
    options?: { nameOnly?: boolean; allowEmpty?: boolean }
  ): Promise<TicketBroker[]> {
    const q = query.trim()
    const allowEmpty = options?.allowEmpty === true
    if (!allowEmpty && q.length < 4) return []
    try {
      const nameOnly = options?.nameOnly === true
      let dbQuery = supabase
        .from('brokers')
        .select('id, name, id_scgp')
        .order('name')
        .limit(50)

      if (q) {
        const num = !nameOnly && /^\d+$/.test(q) ? parseInt(q, 10) : NaN
        if (!isNaN(num)) {
          dbQuery = dbQuery.or(`name.ilike.%${q}%,id_scgp.eq.${num}`)
        } else {
          dbQuery = dbQuery.ilike('name', `%${q}%`)
        }
      }

      const { data } = await dbQuery
      return (data || []) as TicketBroker[]
    } catch {
      return []
    }
  },

  /** @deprecated use searchBrokers */
  async listBrokersForTicketPicker(): Promise<TicketBroker[]> {
    return []
  },

  async upsertTicket(ticket: Partial<QualityTicket>): Promise<QualityTicket | null> {
    const payload = { ...ticket }
    delete (payload as any).stage
    delete (payload as any).category
    delete (payload as any).department
    delete (payload as any).requester
    delete (payload as any).ticket_type
    delete (payload as any).origin
    delete (payload as any).improcedent_reason
    delete (payload as any).broker
    delete (payload as any).deleter
    delete (payload as any).ticket_number
    delete (payload as any).registered_by
    delete (payload as any).registered_by_user_id

    let previous: QualityTicket | null = null
    if (payload.id) {
      const { data: existing } = await supabase
        .from('quality_tickets')
        .select('*')
        .eq('id', payload.id)
        .is('deleted_at', null)
        .maybeSingle()
      previous = existing as QualityTicket | null
    }

    if (!payload.owner_user_id) {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) payload.owner_user_id = user.id
    }
    if (payload.occurred_at) {
      const ymd = parseDateOnlyYmd(payload.occurred_at)
      if (ymd) payload.occurred_at = dateOnlyToTimestamptz(ymd)
    }
    if (!payload.id) {
      const initialStageId = await this.getInitialTicketStageId()
      if (!initialStageId) {
        throw new Error('Etapa inicial (ordem 1) não encontrada. Cadastre em Qualidade > Cadastro > Etapas.')
      }
      payload.stage_id = initialStageId
    }
    if ('policy_number' in payload) {
      payload.policy_number = normalizePolicyNumber(payload.policy_number)
    }
    const { data, error } = await supabase
      .from('quality_tickets')
      .upsert(payload, { onConflict: 'id' })
      .select('*')
      .single()
    if (error) throw error
    const row = normalizeTicketRow(data as QualityTicket)

    if (previous) {
      void logUpdate('quality_incident', row.id, `Qualidade: ticket "${row.title}" atualizado`, snapshotTicketFields(previous) as Record<string, any>, snapshotTicketFields(row) as Record<string, any>)
      if (previous.stage_id !== row.stage_id) {
        const prevStageId = previous.stage_id
        void import('./qualityTicketNotifications')
          .then(({ maybeNotifyRequesterOnTicketCompleted }) =>
            maybeNotifyRequesterOnTicketCompleted(row.id, prevStageId, row.stage_id),
          )
          .catch(err => console.warn('[qualityService.upsertTicket] maybeNotifyRequesterOnTicketCompleted:', err))
      }
    } else {
      void logCreate('quality_incident', row.id, `Qualidade: ticket "${row.title}" criado`, snapshotTicketFields(row) as Record<string, any>)
      void import('./qualityTicketNotifications')
        .then(({ notifyTicketOpenedForConfiguredGroup }) => notifyTicketOpenedForConfiguredGroup(row))
        .catch(err => console.warn('[qualityService.upsertTicket] notifyTicketOpenedForConfiguredGroup:', err))
    }
    return row
  },

  /** @deprecated Use upsertTicket */
  async upsertIncident(ticket: Partial<QualityTicket>): Promise<QualityTicket | null> {
    return this.upsertTicket(ticket)
  },

  async deleteTicket(id: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    const { data: before } = await supabase
      .from('quality_tickets')
      .select('id, title, stage_id')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    const { error } = await supabase
      .from('quality_tickets')
      .update({ deleted_at: new Date().toISOString(), deleted_by: user.id })
      .eq('id', id)
      .is('deleted_at', null)
    if (error) throw error

    const title = (before as QualityTicket | null)?.title || id
    void logDelete('quality_incident', id, `Qualidade: ticket "${title}" excluído (exclusão lógica)`, snapshotTicketFields(before as QualityTicket | null) as Record<string, any>)
  },

  /** @deprecated Use deleteTicket */
  async deleteIncident(id: string): Promise<void> {
    return this.deleteTicket(id)
  },

  async updateTicketStage(ticketId: string, stageId: string): Promise<void> {
    const { data: before } = await supabase
      .from('quality_tickets')
      .select('id, title, stage_id')
      .eq('id', ticketId)
      .is('deleted_at', null)
      .maybeSingle()

    const { error } = await supabase
      .from('quality_tickets')
      .update({ stage_id: stageId })
      .eq('id', ticketId)
      .is('deleted_at', null)
    if (error) throw error

    if (before && (before as QualityTicket).stage_id !== stageId) {
      const t = (before as QualityTicket).title || ticketId
      void logUpdate('quality_incident', ticketId, `Qualidade: ticket "${t}" — etapa alterada`, { stage_id: (before as QualityTicket).stage_id }, { stage_id: stageId })
      void import('./qualityTicketNotifications')
        .then(({ maybeNotifyRequesterOnTicketCompleted }) =>
          maybeNotifyRequesterOnTicketCompleted(ticketId, (before as QualityTicket).stage_id, stageId),
        )
        .catch(err => console.warn('[qualityService.updateTicketStage] maybeNotifyRequesterOnTicketCompleted:', err))
    }
  },

  /** @deprecated Use updateTicketStage */
  async updateIncidentStage(ticketId: string, stageId: string): Promise<void> {
    return this.updateTicketStage(ticketId, stageId)
  },

  // ---- Ticket History ----
  async listHistory(ticketId: string): Promise<QualityTicketHistory[]> {
    const { data } = await supabase
      .from('quality_ticket_history')
      .select('*, changer:changed_by(id, name)')
      .eq('ticket_id', ticketId)
      .order('changed_at', { ascending: false })
    return (data || []) as any
  },

  // ---- Comments ----
  async listComments(ticketId: string): Promise<QualityTicketComment[]> {
    const { data } = await supabase
      .from('quality_ticket_comments')
      .select('*, author:user_id(id, name, user_group_id)')
      .eq('ticket_id', ticketId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true })
    return (data || []) as any
  },

  /**
   * Mapa ticket_id → ISO do primeiro comentário ativo qualificado para SLA de 1ª resposta.
   *
   * - `slaResponseGroupIds` vazio (parâmetro não configurado): usa o primeiro comentário
   *   de **qualquer** autor (comportamento legado).
   * - `slaResponseGroupIds` com um ou mais UUIDs de user_groups: busca em lotes os
   *   comentários + perfis dos autores; por ticket, escolhe o primeiro comentário cujo
   *   autor pertença a **qualquer** um dos grupos.
   *
   * Consulta em lotes de 200 para não estourar o filtro `.in()`.
   */
  async listFirstCommentAtByTicketIds(
    ticketIds: string[],
    slaResponseGroupIds: string[] = [],
  ): Promise<Map<string, string>> {
    const out = new Map<string, string>()
    const ids = Array.from(new Set(ticketIds.filter(Boolean)))
    if (ids.length === 0) return out

    const chunkSize = 200
    const groupSet = new Set(slaResponseGroupIds.filter(Boolean))

    if (groupSet.size === 0) {
      // Legado: menor created_at por ticket, qualquer autor
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize)
        const { data, error } = await supabase
          .from('quality_ticket_comments')
          .select('ticket_id, created_at')
          .in('ticket_id', chunk)
          .is('deleted_at', null)
        if (error) {
          console.warn('[qualityService] listFirstCommentAtByTicketIds:', error)
          continue
        }
        for (const row of data || []) {
          const tid = row.ticket_id as string
          const created = row.created_at as string
          const prev = out.get(tid)
          if (!prev || created < prev) out.set(tid, created)
        }
      }
      return out
    }

    // Com grupo(s) configurado(s): buscar comentários + user_group_id do autor
    type CommentRow = { ticket_id: string; created_at: string; user_id: string; author: { user_group_id: string | null } | null }
    const allComments: CommentRow[] = []

    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize)
      const { data, error } = await supabase
        .from('quality_ticket_comments')
        .select('ticket_id, created_at, user_id, author:user_id(user_group_id)')
        .in('ticket_id', chunk)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })
      if (error) {
        console.warn('[qualityService] listFirstCommentAtByTicketIds (grupo):', error)
        continue
      }
      allComments.push(...((data || []) as unknown as CommentRow[]))
    }

    // Para cada ticket, encontrar o primeiro comentário cujo autor está em algum grupo
    const byTicket = new Map<string, CommentRow[]>()
    for (const row of allComments) {
      const list = byTicket.get(row.ticket_id) ?? []
      list.push(row)
      byTicket.set(row.ticket_id, list)
    }

    for (const [tid, rows] of Array.from(byTicket.entries())) {
      // Rows já vêm ordenados por created_at asc (da query); primeiro autor em qualquer grupo
      const qualified = rows.find((r: CommentRow) => {
        const gid = r.author?.user_group_id
        return gid != null && groupSet.has(gid)
      })
      if (qualified) out.set(tid, qualified.created_at)
    }

    return out
  },

  async addComment(ticketId: string, content: string): Promise<QualityTicketComment> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')
    const { data, error } = await supabase
      .from('quality_ticket_comments')
      .insert({ ticket_id: ticketId, user_id: user.id, content })
      .select('*, author:user_id(id, name, user_group_id)')
      .single()
    if (error) throw error
    const row = data as QualityTicketComment
    void import('./qualityTicketNotifications')
      .then(({ notifyOnQualityTicketComment }) => notifyOnQualityTicketComment(row))
      .catch(err => console.warn('[qualityService.addComment] notifyOnQualityTicketComment:', err))
    return row as any
  },

  async deleteComment(id: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    const { data: before } = await supabase
      .from('quality_ticket_comments')
      .select('id, ticket_id, content')
      .eq('id', id)
      .eq('user_id', user.id)
      .is('deleted_at', null)
      .maybeSingle()

    const { error } = await supabase
      .from('quality_ticket_comments')
      .update({ deleted_at: new Date().toISOString(), deleted_by: user.id })
      .eq('id', id)
      .eq('user_id', user.id)
      .is('deleted_at', null)
    if (error) throw error

    const comment = before as { ticket_id: string; content?: string } | null
    if (comment?.ticket_id) {
      void logDelete('quality_incident', comment.ticket_id, 'Qualidade: comentário excluído no ticket', {
        comment_id: id,
        content_preview: (comment.content || '').slice(0, 200),
      })
    }
  },

  // ---- Ticket Types ----
  async listTicketTypes(): Promise<QualityTicketType[]> {
    const { data } = await supabase
      .from('quality_ticket_types')
      .select('*')
      .is('deleted_at', null)
      .order('display_name', { ascending: true })
    return (data || []) as QualityTicketType[]
  },

  async listTicketTypesWithOrigins(): Promise<QualityTicketType[]> {
    const { data } = await supabase
      .from('quality_ticket_types')
      .select('*, origins:quality_ticket_type_origins(origin_id)')
      .is('deleted_at', null)
      .order('display_name', { ascending: true })
    return ((data || []) as any[]).map(row => ({
      ...row,
      origin_ids: (row.origins || []).map((l: { origin_id: string }) => l.origin_id),
      origins: undefined,
    })) as QualityTicketType[]
  },

  /** @deprecated Use listTicketTypes */
  async listIncidentTypes(): Promise<QualityTicketType[]> { return this.listTicketTypes() },

  async listTicketTypesActive(): Promise<QualityTicketType[]> {
    const { data } = await supabase
      .from('quality_ticket_types')
      .select('*')
      .is('deleted_at', null)
      .eq('is_active', true)
      .order('display_name', { ascending: true })
    return (data || []) as QualityTicketType[]
  },

  /** @deprecated Use listTicketTypesActive */
  async listIncidentTypesActive(): Promise<QualityTicketType[]> { return this.listTicketTypesActive() },

  async listTicketTypesDeleted(): Promise<QualityTicketType[]> {
    const { data } = await supabase
      .from('quality_ticket_types')
      .select('*, deleter:deleted_by(id, name)')
      .not('deleted_at', 'is', null)
      .order('deleted_at', { ascending: false })
    return (data || []) as any
  },

  /** @deprecated Use listTicketTypesDeleted */
  async listIncidentTypesDeleted(): Promise<QualityTicketType[]> { return this.listTicketTypesDeleted() },

  /** Retorna todos os vínculos tipo → origem como mapa `typeId → originId[]`. */
  async listTicketTypeOriginLinks(): Promise<Map<string, string[]>> {
    const { data } = await supabase
      .from('quality_ticket_type_origins')
      .select('ticket_type_id, origin_id')
    const map = new Map<string, string[]>()
    for (const row of (data || []) as QualityTicketTypeOriginLink[]) {
      const list = map.get(row.ticket_type_id) || []
      list.push(row.origin_id)
      map.set(row.ticket_type_id, list)
    }
    return map
  },

  /** Substitui todos os vínculos de um tipo de forma atômica (delete + insert). */
  async setTicketTypeOrigins(typeId: string, originIds: string[]): Promise<void> {
    const { error: delError } = await supabase
      .from('quality_ticket_type_origins')
      .delete()
      .eq('ticket_type_id', typeId)
    if (delError) throw delError

    if (originIds.length === 0) return

    const { error: insError } = await supabase
      .from('quality_ticket_type_origins')
      .insert(originIds.map(origin_id => ({ ticket_type_id: typeId, origin_id })))
    if (insError) throw insError
  },

  /** Lista tipos ativos que possuem pelo menos um vínculo com a origem informada. */
  async listTicketTypesActiveByOrigin(originId: string): Promise<QualityTicketType[]> {
    const { data } = await supabase
      .from('quality_ticket_type_origins')
      .select('ticket_type:ticket_type_id(*, origins:quality_ticket_type_origins(origin_id))')
      .eq('origin_id', originId)
    const types: QualityTicketType[] = []
    for (const row of (data || []) as any[]) {
      const t = row.ticket_type
      if (!t || t.deleted_at || !t.is_active) continue
      types.push({
        ...t,
        origin_ids: (t.origins || []).map((l: { origin_id: string }) => l.origin_id),
        origins: undefined,
      })
    }
    types.sort((a, b) => a.display_name.localeCompare(b.display_name, 'pt-BR'))
    return types
  },

  async upsertTicketType(item: Partial<QualityTicketType> & { origin_ids?: string[] }): Promise<QualityTicketType | null> {
    const originIds = item.origin_ids
    const payload = { ...item }
    delete (payload as any).deleter
    delete (payload as any).origin_ids
    delete (payload as any).origins

    let previous: QualityTicketType | null = null
    if (payload.id) {
      const { data: existing } = await supabase
        .from('quality_ticket_types')
        .select('*')
        .eq('id', payload.id)
        .is('deleted_at', null)
        .maybeSingle()
      previous = existing as QualityTicketType | null
    }

    const { data, error } = await supabase
      .from('quality_ticket_types')
      .upsert(payload, { onConflict: 'id' })
      .select('*')
      .single()
    if (error) throw error
    const row = data as QualityTicketType

    if (originIds !== undefined) {
      await this.setTicketTypeOrigins(row.id, originIds)
    }

    if (previous) {
      void logUpdate('quality_incident_type', row.id, `Qualidade: tipo "${row.display_name}" atualizado`, { name: previous.name, display_name: previous.display_name }, { name: row.name, display_name: row.display_name })
    } else {
      void logCreate('quality_incident_type', row.id, `Qualidade: tipo "${row.display_name}" criado`, { name: row.name, display_name: row.display_name })
    }
    return row
  },

  /** @deprecated Use upsertTicketType */
  async upsertIncidentType(item: Partial<QualityTicketType>): Promise<QualityTicketType | null> { return this.upsertTicketType(item) },

  async deleteTicketType(id: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    const { data: before } = await supabase
      .from('quality_ticket_types')
      .select('id, name, display_name')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    const { error } = await supabase
      .from('quality_ticket_types')
      .update({ deleted_at: new Date().toISOString(), deleted_by: user.id, is_active: false })
      .eq('id', id)
      .is('deleted_at', null)
    if (error) throw error

    const label = (before as QualityTicketType | null)?.display_name || id
    void logDelete('quality_incident_type', id, `Qualidade: tipo "${label}" excluído`, { name: label })
  },

  /** @deprecated Use deleteTicketType */
  async deleteIncidentType(id: string): Promise<void> { return this.deleteTicketType(id) },

  // ---- Ticket Origins ----
  async listTicketOrigins(): Promise<QualityTicketOrigin[]> {
    const { data } = await supabase
      .from('quality_ticket_origins')
      .select('*')
      .is('deleted_at', null)
      .order('display_name', { ascending: true })
    return (data || []) as QualityTicketOrigin[]
  },

  /** @deprecated Use listTicketOrigins */
  async listIncidentOrigins(): Promise<QualityTicketOrigin[]> { return this.listTicketOrigins() },

  async listTicketOriginsActive(): Promise<QualityTicketOrigin[]> {
    const { data } = await supabase
      .from('quality_ticket_origins')
      .select('*')
      .is('deleted_at', null)
      .eq('is_active', true)
      .order('display_name', { ascending: true })
    return (data || []) as QualityTicketOrigin[]
  },

  /** @deprecated Use listTicketOriginsActive */
  async listIncidentOriginsActive(): Promise<QualityTicketOrigin[]> { return this.listTicketOriginsActive() },

  async listTicketOriginsDeleted(): Promise<QualityTicketOrigin[]> {
    const { data } = await supabase
      .from('quality_ticket_origins')
      .select('*, deleter:deleted_by(id, name)')
      .not('deleted_at', 'is', null)
      .order('deleted_at', { ascending: false })
    return (data || []) as any
  },

  /** @deprecated Use listTicketOriginsDeleted */
  async listIncidentOriginsDeleted(): Promise<QualityTicketOrigin[]> { return this.listTicketOriginsDeleted() },

  async upsertTicketOrigin(item: Partial<QualityTicketOrigin>): Promise<QualityTicketOrigin | null> {
    const payload = { ...item }
    delete (payload as any).deleter

    let previous: QualityTicketOrigin | null = null
    if (payload.id) {
      const { data: existing } = await supabase
        .from('quality_ticket_origins')
        .select('*')
        .eq('id', payload.id)
        .is('deleted_at', null)
        .maybeSingle()
      previous = existing as QualityTicketOrigin | null
    }

    const { data, error } = await supabase
      .from('quality_ticket_origins')
      .upsert(payload, { onConflict: 'id' })
      .select('*')
      .single()
    if (error) throw error
    const row = data as QualityTicketOrigin

    if (previous) {
      void logUpdate('quality_incident_origin', row.id, `Qualidade: origem "${row.display_name}" atualizada`, { name: previous.name, display_name: previous.display_name }, { name: row.name, display_name: row.display_name })
    } else {
      void logCreate('quality_incident_origin', row.id, `Qualidade: origem "${row.display_name}" criada`, { name: row.name, display_name: row.display_name })
    }
    return row
  },

  /** @deprecated Use upsertTicketOrigin */
  async upsertIncidentOrigin(item: Partial<QualityTicketOrigin>): Promise<QualityTicketOrigin | null> { return this.upsertTicketOrigin(item) },

  async deleteTicketOrigin(id: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    const { data: before } = await supabase
      .from('quality_ticket_origins')
      .select('id, name, display_name')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    const { error } = await supabase
      .from('quality_ticket_origins')
      .update({ deleted_at: new Date().toISOString(), deleted_by: user.id, is_active: false })
      .eq('id', id)
      .is('deleted_at', null)
    if (error) throw error

    const label = (before as QualityTicketOrigin | null)?.display_name || id
    void logDelete('quality_incident_origin', id, `Qualidade: origem "${label}" excluída`, { name: label })
  },

  /** @deprecated Use deleteTicketOrigin */
  async deleteIncidentOrigin(id: string): Promise<void> { return this.deleteTicketOrigin(id) },

  // ---- Improcedent reasons (registo improcedente) ----
  async listImprocedentReasons(): Promise<QualityImprocedentReason[]> {
    const { data } = await supabase
      .from('quality_improcedent_reasons')
      .select('*')
      .is('deleted_at', null)
      .order('display_name', { ascending: true })
    return (data || []) as QualityImprocedentReason[]
  },

  async listImprocedentReasonsActive(): Promise<QualityImprocedentReason[]> {
    const { data } = await supabase
      .from('quality_improcedent_reasons')
      .select('*')
      .is('deleted_at', null)
      .eq('is_active', true)
      .order('display_name', { ascending: true })
    return (data || []) as QualityImprocedentReason[]
  },

  async upsertImprocedentReason(item: Partial<QualityImprocedentReason>): Promise<QualityImprocedentReason | null> {
    const payload = { ...item }
    delete (payload as { deleter?: unknown }).deleter

    let previous: QualityImprocedentReason | null = null
    if (payload.id) {
      const { data: existing } = await supabase
        .from('quality_improcedent_reasons')
        .select('*')
        .eq('id', payload.id)
        .is('deleted_at', null)
        .maybeSingle()
      previous = existing as QualityImprocedentReason | null
    }

    const { data, error } = await supabase
      .from('quality_improcedent_reasons')
      .upsert(payload, { onConflict: 'id' })
      .select('*')
      .single()
    if (error) throw error
    const row = data as QualityImprocedentReason

    if (previous) {
      void logUpdate('quality_improcedent_reason', row.id, `Qualidade: registo improcedente "${row.display_name}" atualizado`, snapshotCategoryFields(previous), snapshotCategoryFields(row))
    } else {
      void logCreate('quality_improcedent_reason', row.id, `Qualidade: registo improcedente "${row.display_name}" criado`, snapshotCategoryFields(row))
    }
    return row
  },

  async deleteImprocedentReason(id: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    const { data: before } = await supabase
      .from('quality_improcedent_reasons')
      .select('id, name, display_name')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    const { error } = await supabase
      .from('quality_improcedent_reasons')
      .update({ deleted_at: new Date().toISOString(), deleted_by: user.id, is_active: false })
      .eq('id', id)
      .is('deleted_at', null)
    if (error) throw error

    const label = (before as QualityImprocedentReason | null)?.display_name || id
    void logDelete('quality_improcedent_reason', id, `Qualidade: registo improcedente "${label}" excluído`, { name: label })
  },

  // ---- Departments (tabela existente) ----
  async listDepartmentsActive(): Promise<{ id: string; name: string; display_name: string }[]> {
    const { data } = await supabase
      .from('departments')
      .select('id, name, display_name')
      .eq('is_active', true)
      .order('display_name', { ascending: true })
    return (data || []) as any
  },

  // ---- Ticket Attachments ----
  async listTicketAttachments(ticketId: string): Promise<QualityTicketAttachment[]> {
    const { data } = await supabase
      .from('quality_ticket_attachments')
      .select('*')
      .eq('ticket_id', ticketId)
      .is('comment_id', null)
      .order('created_at', { ascending: true })
    return (data || []) as QualityTicketAttachment[]
  },

  async listCommentAttachmentsByTicketId(ticketId: string): Promise<QualityTicketAttachment[]> {
    const { data } = await supabase
      .from('quality_ticket_attachments')
      .select('*')
      .eq('ticket_id', ticketId)
      .not('comment_id', 'is', null)
      .order('created_at', { ascending: true })
    return (data || []) as QualityTicketAttachment[]
  },

  async uploadTicketAttachment(
    ticketId: string,
    file: File,
    commentId?: string,
  ): Promise<QualityTicketAttachment | null> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Usuário não autenticado')

    if (file.size > TICKET_ATTACHMENT_MAX_BYTES) {
      throw new Error(`O arquivo "${file.name}" excede o limite de ${TICKET_ATTACHMENT_MAX_MB} MB.`)
    }

    const ext = file.name.split('.').pop() || 'bin'
    const filePath = `${ticketId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`
    const mimeType = (() => {
      if (file.type) return file.type
      const lower = file.name.toLowerCase()
      if (lower.endsWith('.msg')) return 'application/vnd.ms-outlook'
      if (lower.endsWith('.eml')) return 'message/rfc822'
      if (lower.endsWith('.pdf')) return 'application/pdf'
      return null
    })()

    const { error: storageError } = await supabase.storage
      .from('quality-tickets')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        ...(mimeType ? { contentType: mimeType } : {}),
      })

    if (storageError) throw storageError

    const { data, error: dbError } = await supabase
      .from('quality_ticket_attachments')
      .insert({
        ticket_id: ticketId,
        file_path: filePath,
        file_name: file.name,
        file_size: file.size,
        mime_type: mimeType || file.type || null,
        uploaded_by: user.id,
        ...(commentId ? { comment_id: commentId } : {}),
      })
      .select()
      .single()

    if (dbError) throw dbError
    const row = data as QualityTicketAttachment
    void logUpdate(
      'quality_incident',
      ticketId,
      commentId
        ? `Qualidade: anexo adicionado ao comentário — ${file.name}`
        : `Qualidade: anexo adicionado ao ticket — ${file.name}`,
      {},
      { file_name: file.name, attachment_id: row.id, ...(commentId ? { comment_id: commentId } : {}) },
    )
    return row
  },

  async deleteTicketAttachment(attachment: QualityTicketAttachment): Promise<void> {
    await supabase.storage.from('quality-tickets').remove([attachment.file_path])
    await supabase
      .from('quality_ticket_attachments')
      .delete()
      .eq('id', attachment.id)

    void logUpdate(
      'quality_incident',
      attachment.ticket_id,
      `Qualidade: anexo removido do ticket — ${attachment.file_name}`,
      { file_name: attachment.file_name, attachment_id: attachment.id },
      {},
    )
  },

  async getAttachmentSignedUrl(filePath: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from('quality-tickets')
      .createSignedUrl(filePath, 3600)
    if (error) throw error
    return data.signedUrl
  },

  // ---- Parâmetros ----

  async listQualityParameters(): Promise<QualityParameter[]> {
    const { data, error } = await supabase
      .from('quality_parameters')
      .select('*')
      .order('key', { ascending: true })
    if (error) {
      console.warn('[qualityService] listQualityParameters error:', error)
      return []
    }
    return (data || []) as QualityParameter[]
  },

  getQualityParameterValueByKey,

  async updateQualityParameter(
    id: string,
    payload: { value: string; description?: string }
  ): Promise<QualityParameter> {
    const { data: existing } = await supabase
      .from('quality_parameters')
      .select('*')
      .eq('id', id)
      .maybeSingle()
    const previous = existing as QualityParameter | null

    const update: { value: string; description?: string } = { value: payload.value }
    if (payload.description !== undefined) update.description = payload.description
    const { data, error } = await supabase
      .from('quality_parameters')
      .update(update)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    const row = data as QualityParameter

    void logUpdate(
      'parameter',
      row.id,
      `Qualidade: parâmetro "${row.key}" atualizado`,
      { value: previous?.value ?? null },
      { value: row.value },
    )

    return row
  },
}
